/**
 * Przycisk z ikoną tabeli obok pola wyboru użytkownika (np. „Opiekun" na fakturze) — otwiera
 * TĘ SAMĄ tabelę wyboru co przy kontrahencie (`RecordTablePicker`, entity='users', single-pick):
 * pełna szerokość, kolumny z katalogu pól, filtry w nagłówkach. Mirror `VendorTablePickerButton`
 * (`components/invoices/vendorPicker.tsx`): akcje wiersza „Przejdź do kartoteki" (strona
 * użytkownika) i „Edytuj" (karta użytkownika w oknie NAD tabelą, bez wychodzenia z dokumentu).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Table } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { RecordTablePicker } from '@/components/ui/RecordTablePicker';
import { UserFormModal } from '@/components/users/UserFormModal';

export interface UserTablePickerButtonProps {
  value: string | null;
  onPick: (userId: string) => void;
  companyId?: string | null;
  disabled?: boolean;
  'data-testid'?: string;
}

export function UserTablePickerButton({
  value,
  onPick,
  companyId,
  disabled,
  'data-testid': testId,
}: UserTablePickerButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);

  return (
    <>
      <IconButton
        variant="subtle"
        size="md"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label={t('ui.userTablePicker.title')}
        title={t('ui.userTablePicker.title')}
        data-testid={testId}
      >
        <Table />
      </IconButton>
      <RecordTablePicker
        entity="users"
        open={open}
        onClose={() => setOpen(false)}
        companyId={companyId ?? undefined}
        value={value ?? undefined}
        data-testid={testId ? `${testId}-picker` : undefined}
        onChange={(id) => {
          onPick(id);
          setOpen(false);
        }}
        recordActions={{
          // „Przejdź do kartoteki" — pełna karta użytkownika pod route'em (zamyka okno wyboru).
          onOpenCard: (row) => {
            setOpen(false);
            navigate(`/users/${row.id}`);
          },
          // „Edytuj" — karta użytkownika w modalu NAD oknem wyboru, bez wychodzenia.
          onEditRecord: (row) => setEditUserId(row.id),
          labels: {
            openCard: t('ui.userTablePicker.openCard'),
            editRecord: t('ui.userTablePicker.editRecord'),
            createNew: '',
          },
        }}
      />
      <UserFormModal
        open={editUserId !== null}
        userId={editUserId ?? undefined}
        onClose={() => setEditUserId(null)}
        data-testid={testId ? `${testId}-form` : undefined}
      />
    </>
  );
}
